/**
 * 成本构成组件
 */
import React from 'react';
import { Typography, Progress, Space, Button } from 'antd';
import {
  UserOutlined,
  ToolOutlined,
  GoldOutlined,
  ApartmentOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { formatMoney } from '../../utils/budgetUtils';

const { Text, Title } = Typography;

export type CostDetailType = 'worktime' | 'equipment' | 'material' | 'subcontract' | 'coordination';

interface CostBreakdownProps {
  stats: any;
  isMobile?: boolean;
  onViewDetail?: (type: CostDetailType) => void;
  onAddRecord?: (type: CostDetailType) => void;
}

const CostBreakdown: React.FC<CostBreakdownProps> = ({
  stats,
  isMobile = false,
  onViewDetail,
  onAddRecord,
}) => {
  const workTimeCost = stats?.summary?.costBreakdown?.workTimeCost || 0;
  const equipmentCost = stats?.summary?.costBreakdown?.equipmentCost || 0;
  const materialCost = stats?.summary?.costBreakdown?.materialCost || 0;
  const subcontractCost = stats?.summary?.costBreakdown?.subcontractCost || 0;
  const coordinationCost = stats?.summary?.costBreakdown?.coordinationCost || 0;

  const workTimePercent = stats?.summary?.costPercentage?.workTimePercentage || 0;
  const equipmentPercent = stats?.summary?.costPercentage?.equipmentPercentage || 0;
  const materialPercent = stats?.summary?.costPercentage?.materialPercentage || 0;
  const subcontractPercent = stats?.summary?.costPercentage?.subcontractPercentage || 0;
  const coordinationPercent = stats?.summary?.costPercentage?.coordinationPercentage || 0;

  const costItems = [
    {
      key: 'worktime' as CostDetailType,
      icon: <UserOutlined style={{ color: '#547590' }} />,
      label: '人工成本',
      cost: workTimeCost,
      percent: workTimePercent,
      color: '#547590',
      canAdd: true,
    },
    {
      key: 'equipment' as CostDetailType,
      icon: <ToolOutlined style={{ color: '#5ad1b2' }} />,
      label: '机械成本',
      cost: equipmentCost,
      percent: equipmentPercent,
      color: '#5ad1b2',
      canAdd: true,
    },
    {
      key: 'material' as CostDetailType,
      icon: <GoldOutlined style={{ color: '#b99258' }} />,
      label: '材料成本',
      cost: materialCost,
      percent: materialPercent,
      color: '#b99258',
      canAdd: true,
    },
    {
      key: 'subcontract' as CostDetailType,
      icon: <TeamOutlined style={{ color: '#9384ff' }} />,
      label: '分包成本',
      cost: subcontractCost,
      percent: subcontractPercent,
      color: '#9384ff',
      canAdd: false,
    },
    {
      key: 'coordination' as CostDetailType,
      icon: <ApartmentOutlined style={{ color: '#6ee7d8' }} />,
      label: '协调成本',
      cost: coordinationCost,
      percent: coordinationPercent,
      color: '#6ee7d8',
      canAdd: false,
    }
  ];

  return (
    <div
      style={{
        marginBottom: isMobile ? 16 : 24,
        padding: isMobile ? 14 : 18,
        borderRadius: 20,
        background: 'rgba(210, 218, 224, 0.5)',
        border: '1px solid rgba(16, 27, 35, 0.08)'
      }}
    >
      <Title level={5} style={{ marginBottom: 16, fontSize: isMobile ? 14 : 16, color: '#10171d' }}>
        成本构成
      </Title>

      {costItems.map((item, index) => (
        <div key={index} style={{ marginBottom: index === costItems.length - 1 ? 0 : 14 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 8,
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : 220 }}>
              <Space size={8}>
                {item.icon}
                <Text style={{ fontSize: isMobile ? 13 : 14, color: '#10171d' }}>{item.label}</Text>
              </Space>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Space size={8}>
                <Text strong style={{ color: item.color, fontSize: isMobile ? 13 : 14 }}>
                  {formatMoney(item.cost, false)}
                </Text>
                <Text style={{ fontSize: isMobile ? 11 : 12, color: '#667986' }}>
                  ({item.percent.toFixed(1)}%)
                </Text>
              </Space>

              <Space size={4} wrap>
                <Button size="small" type="link" onClick={() => onViewDetail?.(item.key)} style={{ paddingInline: 4, color: '#b99258' }}>
                  查看明细
                </Button>
                {item.canAdd && (
                  <Button size="small" type="link" onClick={() => onAddRecord?.(item.key)} style={{ paddingInline: 4, color: '#547590' }}>
                    补录
                  </Button>
                )}
              </Space>
            </div>
          </div>
          <Progress percent={item.percent} strokeColor={item.color} trailColor="rgba(16, 27, 35, 0.08)" showInfo={false} size="small" />
        </div>
      ))}
    </div>
  );
};

export default CostBreakdown;
